import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Button,
  IconButton,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import {
  PhoneRounded as PhoneIcon,
  EmailRounded as EmailIcon,
  LocationOnRounded as LocationIcon,
  CakeRounded as CakeIcon,
  EditRounded as EditIcon,
  DeleteRounded as DeleteIcon,
  ContactPageRounded as VCardIcon,
  ArrowBackRounded as BackIcon,
  PersonAddRounded as PersonAddIcon,
  LinkOffRounded as RemoveRelationshipIcon,
} from '@mui/icons-material';
import { GET_PERSON, GET_PERSONS, GET_RELATIONSHIPS } from '../apollo/queries';
import { DELETE_PERSON, REMOVE_RELATIONSHIP } from '../apollo/mutations';
import {
  getDisplayName,
  getInitials,
  calculateAge,
  formatBirthday,
  getDaysUntilBirthday,
  downloadVCard,
  getPersonAvatarUrl,
  getPersonProfilePictureUrl,
} from '../hooks/usePersonHelpers';
import PersonFormDialog from '../components/PersonFormDialog';
import AddRelationshipDialog from '../components/AddRelationshipDialog';

function PersonDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showRelationshipDialog, setShowRelationshipDialog] = useState(false);
  const [relationshipType, setRelationshipType] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);

  const { data, loading, error } = useQuery(GET_PERSON, { variables: { id } });
  const { data: relationshipsData } = useQuery(GET_RELATIONSHIPS);

  const [deletePerson, { loading: deleting }] = useMutation(DELETE_PERSON, {
    variables: { id },
    refetchQueries: [{ query: GET_PERSONS }],
    onCompleted: () => navigate('/directory'),
  });

  const [removeRelationship] = useMutation(REMOVE_RELATIONSHIP, {
    refetchQueries: [{ query: GET_PERSON, variables: { id } }, { query: GET_RELATIONSHIPS }],
  });

  // Helper to find relationship ID between this person and another
  const findRelationshipId = (otherPersonId, type) => {
    if (!relationshipsData?.relationships) return null;

    const rel = relationshipsData.relationships.find((r) => {
      if (type === 'PARENT_CHILD') {
        // For parent: this person is child (toPersonId), other is parent (fromPersonId)
        // For child: this person is parent (fromPersonId), other is child (toPersonId)
        return (
          (r.fromPersonId === id && r.toPersonId === otherPersonId && r.type === 'PARENT_CHILD') ||
          (r.fromPersonId === otherPersonId && r.toPersonId === id && r.type === 'PARENT_CHILD')
        );
      } else {
        // For spouse/partner, check both directions
        return (
          ((r.fromPersonId === id && r.toPersonId === otherPersonId) ||
           (r.fromPersonId === otherPersonId && r.toPersonId === id)) &&
          (r.type === 'SPOUSE' || r.type === 'PARTNER')
        );
      }
    });

    return rel?.id;
  };

  const handleRemoveRelationship = (relId) => {
    if (relId && window.confirm('Remove this relationship?')) {
      removeRelationship({ variables: { id: relId } });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data?.person) {
    return <Alert severity="error">Person not found</Alert>;
  }

  const person = data.person;
  const age = calculateAge(person.birthday, person.deceasedDate);
  const daysUntilBirthday = person.deceasedDate ? null : getDaysUntilBirthday(person.birthday);

  const handleAddChild = () => {
    setRelationshipType('PARENT_CHILD');
    setShowRelationshipDialog(true);
  };

  const handleAddSpouse = () => {
    setRelationshipType('SPOUSE');
    setShowRelationshipDialog(true);
  };

  const getGoogleMapsUrl = () => {
    const h = person.household;
    if (!h) return '#';
    const parts = [h.addressLine1, h.city, h.state, h.postalCode, h.country].filter(Boolean);
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts.join(', '))}`;
  };

  return (
    <Box>
      <Button startIcon={<BackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Back
      </Button>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, alignItems: { sm: 'flex-start' } }}>
            <Avatar
              src={getPersonProfilePictureUrl(person, 120)}
              alt={`Photo of ${getDisplayName(person)}`}
              sx={{
                width: 120,
                height: 120,
                mx: { xs: 'auto', sm: 0 },
                cursor: person.profilePictureUrl ? 'pointer' : 'default',
                transition: 'transform 0.2s ease',
                '&:hover': person.profilePictureUrl ? { transform: 'scale(1.05)' } : {},
              }}
              onClick={() => person.profilePictureUrl && setShowPhotoDialog(true)}
            >
              <Typography variant="h3">{getInitials(person)}</Typography>
            </Avatar>

            <Box sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                <Box>
                  <Typography variant="h4" fontWeight={600}>
                    {getDisplayName(person)}
                  </Typography>
                  {person.deceasedDate && (
                    <Chip label="Deceased" color="default" size="small" sx={{ mt: 1 }} />
                  )}
                </Box>
                <Box>
                  <IconButton onClick={() => setShowEditDialog(true)} color="primary">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => downloadVCard(person)} color="primary">
                    <VCardIcon />
                  </IconButton>
                  <IconButton onClick={() => setShowDeleteConfirm(true)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>

              {age !== null && (
                <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>
                  {person.deceasedDate ? `Lived to age ${age}` : `${age} years old`}
                </Typography>
              )}

              {person.birthday && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <CakeIcon color="action" fontSize="small" />
                  <Typography variant="body1">
                    {formatBirthday(person.birthday)}
                    {daysUntilBirthday !== null && daysUntilBirthday <= 30 && (
                      <Chip
                        label={daysUntilBirthday === 0 ? "Today! 🎉" : `${daysUntilBirthday} days`}
                        size="small"
                        color="secondary"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Contact Information</Typography>
              <Divider sx={{ mb: 2 }} />
              {person.phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <PhoneIcon color="action" />
                  <Typography component="a" href={`tel:${person.phone}`} sx={{ color: 'primary.main' }}>
                    {person.phone}
                  </Typography>
                </Box>
              )}
              {person.email && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <EmailIcon color="action" />
                  <Typography component="a" href={`mailto:${person.email}`} sx={{ color: 'primary.main' }}>
                    {person.email}
                  </Typography>
                </Box>
              )}
              {person.household && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <LocationIcon color="action" />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {person.household.name}
                    </Typography>
                    {person.household.addressLine1 && (
                      <Typography component="a" href={getGoogleMapsUrl()} target="_blank" sx={{ color: 'primary.main' }}>
                        {person.household.addressLine1}<br />
                        {person.household.addressLine2 && <>{person.household.addressLine2}<br /></>}
                        {[person.household.city, person.household.state, person.household.postalCode].filter(Boolean).join(', ')}<br />
                        {person.household.country}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              {!person.phone && !person.email && !person.household && (
                <Typography color="text.secondary">No contact info available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Family Relationships</Typography>
              <Divider sx={{ mb: 2 }} />

              {person.parents?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">Parents</Typography>
                  <List dense>
                    {person.parents.map((parent) => {
                      const relId = findRelationshipId(parent.id, 'PARENT_CHILD');
                      return (
                        <ListItem key={parent.id} sx={{ pr: 6 }}>
                          <ListItemAvatar>
                            <Avatar
                              component={Link}
                              to={`/person/${parent.id}`}
                              src={getPersonAvatarUrl(parent, 32)}
                              sx={{ width: 32, height: 32, cursor: 'pointer' }}
                            >
                              {getInitials(parent)}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Link to={`/person/${parent.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                {getDisplayName(parent)}
                              </Link>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Tooltip title="Remove relationship">
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => handleRemoveRelationship(relId)}
                              >
                                <RemoveRelationshipIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              )}

              {person.spouses?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">Spouse / Partner</Typography>
                  <List dense>
                    {person.spouses.map((spouse) => {
                      const relId = findRelationshipId(spouse.id, 'SPOUSE');
                      return (
                        <ListItem key={spouse.id} sx={{ pr: 6 }}>
                          <ListItemAvatar>
                            <Avatar
                              component={Link}
                              to={`/person/${spouse.id}`}
                              src={getPersonAvatarUrl(spouse, 32)}
                              sx={{ width: 32, height: 32, cursor: 'pointer' }}
                            >
                              {getInitials(spouse)}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Link to={`/person/${spouse.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                {getDisplayName(spouse)}
                              </Link>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Tooltip title="Remove relationship">
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => handleRemoveRelationship(relId)}
                              >
                                <RemoveRelationshipIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              )}

              {person.children?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">Children</Typography>
                  <List dense>
                    {person.children.map((child) => {
                      const relId = findRelationshipId(child.id, 'PARENT_CHILD');
                      return (
                        <ListItem key={child.id} sx={{ pr: 6 }}>
                          <ListItemAvatar>
                            <Avatar
                              component={Link}
                              to={`/person/${child.id}`}
                              src={getPersonAvatarUrl(child, 32)}
                              sx={{ width: 32, height: 32, cursor: 'pointer' }}
                            >
                              {getInitials(child)}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Link to={`/person/${child.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                {getDisplayName(child)}
                              </Link>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Tooltip title="Remove relationship">
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => handleRemoveRelationship(relId)}
                              >
                                <RemoveRelationshipIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Button size="small" startIcon={<PersonAddIcon />} onClick={handleAddChild}>
                  Add Child
                </Button>
                <Button size="small" startIcon={<PersonAddIcon />} onClick={handleAddSpouse}>
                  Add Spouse
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {person.notes && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Notes</Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{person.notes}</Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      <PersonFormDialog open={showEditDialog} onClose={() => setShowEditDialog(false)} person={person} />

      <AddRelationshipDialog
        open={showRelationshipDialog}
        onClose={() => setShowRelationshipDialog(false)}
        personId={id}
        type={relationshipType}
      />

      <Dialog open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)}>
        <DialogTitle>Delete {getDisplayName(person)}?</DialogTitle>
        <DialogContent>
          <Typography>This action cannot be undone. All relationships will also be removed.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
          <Button onClick={() => deletePerson()} color="error" disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Photo Lightbox Dialog */}
      <Dialog
        open={showPhotoDialog}
        onClose={() => setShowPhotoDialog(false)}
        maxWidth="md"
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
          },
        }}
      >
        <Box
          component="img"
          src={getPersonProfilePictureUrl(person, 600)}
          alt={`Photo of ${getDisplayName(person)}`}
          onClick={() => setShowPhotoDialog(false)}
          sx={{
            maxWidth: '90vw',
            maxHeight: '80vh',
            objectFit: 'contain',
            borderRadius: 2,
            cursor: 'pointer',
          }}
        />
      </Dialog>
    </Box>
  );
}

export default PersonDetailPage;

